<!-- biome-ignore-all lint/correctness/useVueVForKey: Init Biome -->

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { match } from "@/helpers/string";
import { useConfigurationStore } from "@/store/configurationStore";

import type { supporttedEventNames } from "@/dsComponents/Dropdown/types";
import type { LabelValue } from '@/types/common';

import ComboButton from "@/dsComponents/buttons/ComboButton.vue";
import { DropdownInput, DropdownItem } from "@/dsComponents/Dropdown";
import ControlledDropdown from "@/dsComponents/Dropdown/ControlledDropdown.vue";

interface HiringReasonsDropdownProps {
    value: LabelValue | null;
    placeholder?: string;
    iconLeft?: string;
    closable?: boolean;
    size?: "xs" | "sm";
    variant?: "stroke" | "ghost";
    width?: string;
    height?: string;
    fullWidth?: boolean;
}

const props = withDefaults(defineProps<HiringReasonsDropdownProps>(), {
    iconLeft: "code-variable-plus",
    variant: "ghost",
    width: "522px",
    height: "220px",
});

const emit = defineEmits<{
    (event: "input", value: LabelValue | null): void;
    (event: "close"): void;
}>();

const { t } = useI18n();
const configStore = useConfigurationStore();

const hiringReasonSearch = ref("");

const hiringReasons = computed(() => configStore.hiringReasonOptions);

const dropdownHiringReason = computed(() => {
    const options = hiringReasons.value;
    if (!hiringReasonSearch.value.trim()) return options;
    return options.filter((hr) => match(hr.label, hiringReasonSearch.value));
});

const open = ref(false);

const toggle = () => {
    open.value = !open.value;
};

const handleControlledDropdownChange = (event: supporttedEventNames) => {
    if (event === "clickaway") open.value = false;
};

const handleHiringReasonSelect = (hiringReason: string) => {
    if (hiringReason === props.value?.value) {
        emit("input", null);
        open.value = false;
        hiringReasonSearch.value = "";
        return;
    }
    const found = hiringReasons.value.find((j: LabelValue) => j.value === hiringReason);
    if (found) emit("input", found);
    open.value = false;
    hiringReasonSearch.value = "";
};

const buttonLabel = computed(() => {
    if (props.value?.value) {
        if (props.value?.label) return props.value.label;
        // biome-ignore lint/style/noNonNullAssertion: Init Biome
        const found = hiringReasons.value.find((v) => v.value === props.value!.value);
        if (found) return found.label;
    }
    return props.placeholder ?? t("page.order.reason");
});
</script>
<template>
    <div>
        <ControlledDropdown
            :width="width"
            :height="height"
            :open="open"
            :full-width="fullWidth"
            :fit-content="!fullWidth"
            :styles="{ gap: '2px', padding: '4px', width: '522px' }"
            @change="handleControlledDropdownChange"
        >
            <template #trigger-zone>
                <ComboButton
                    :size="size"
                    :focus="open"
                    :icon-left="iconLeft"
                    :icon-right="closable ? 'x' : undefined"
                    :variant="variant"
                    :placeholder="!value"
                    :full-width="fullWidth"
                    @click="toggle"
                    @right="emit('close')"
                >
                    {{ buttonLabel }}
                </ComboButton>
            </template>
            <template #dropdown-content>
                <DropdownInput :focused="open" v-model="hiringReasonSearch" />
                <div class="hiringReason-container">
                    <DropdownItem
                        v-for="hiringReason of dropdownHiringReason"
                        :checked="hiringReason.value === value?.value"
                        font-size="13"
                        font-weight="500"
                        :custom-styles="{ padding: '7px 10px', borderRadius: '4px' }"
                        @click="() => handleHiringReasonSelect(hiringReason.value)"
                    >
                        {{ `${hiringReason.code} - ${hiringReason.label}` }}
                    </DropdownItem>
                </div>
            </template>
        </ControlledDropdown>
    </div>
</template>
<style scoped lang="scss">
.hiringReason-container {
    padding-top: var(--spacing-xs, 4px);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xxs, 2px);
    overflow-y: auto;
}
</style>
